import { i18n } from '@/i18n'
import type { ApiErrorBody, ErrorCode, TokenPairResponse } from './types'

// DI через модульные переменные — разрывает циклическую зависимость
// между auth-слоем и API-клиентом. AuthProvider вызывает configureClient()
// синхронно во время рендера, до первого запроса.
let getAccessToken: () => string | null = () => null
let getRefreshToken: () => string | null = () => null
let onTokenRefreshed: (pair: TokenPairResponse) => void = () => {}
let onSessionExpired: (code?: string | null) => void = () => {}

export interface ConfigureClientOptions {
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
  onTokenRefreshed: (pair: TokenPairResponse) => void
  // code — ErrorCode из тела ответа, если доступен (напр. 'USER_BANNED', 'TOKEN_REUSE').
  onSessionExpired: (code?: string | null) => void
}

export function configureClient(opts: ConfigureClientOptions): void {
  getAccessToken = opts.getAccessToken
  getRefreshToken = opts.getRefreshToken
  onTokenRefreshed = opts.onTokenRefreshed
  onSessionExpired = opts.onSessionExpired
}

export function getApiBaseUrl(): string {
  // window.__CONFIG__ инжектится /config.js перед main.tsx
  // (см. docker-entrypoint.sh для прод, public/config.js для dev).
  const runtime = typeof window !== 'undefined' ? window.__CONFIG__?.apiUrl : undefined
  return runtime ?? import.meta.env.VITE_API_URL ?? ''
}

export function getWsBaseUrl(): string {
  const base = getApiBaseUrl()
  if (!base) {
    // Относительный путь → берём текущий хост, меняем схему.
    if (typeof window === 'undefined') return ''
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${window.location.host}`
  }
  return base.replace(/^http/, 'ws')
}

export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode | null
  // Текст с сервера (всегда русский), у ошибок самого клиента — сообщение на текущем языке,
  // иначе пусто. Заглушку вроде «HTTP 500» сюда не подставлять: getErrorMessage тогда не
  // дошёл бы до перевода по коду.
  readonly detail: string

  constructor(status: number, code: ErrorCode | null, detail: string) {
    super(detail || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  skipAuth?: boolean
  isRetry?: boolean
  // fetch keepalive: запрос переживает закрытие/выгрузку страницы (сохранение на pagehide).
  keepalive?: boolean
}

// Refresh-токен одноразовый: параллельные 401 должны делить ОДНУ попытку
// обновления, иначе reuse-detection отзовёт всю цепочку токенов.
let inflightRefresh: Promise<TokenPairResponse> | null = null

async function performRefresh(): Promise<TokenPairResponse> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new ApiError(
      401,
      'REQUIRED_TOKEN',
      i18n.t(($) => $.errors.noRefreshToken),
    )
  }
  const url = `${getApiBaseUrl()}/v0/auth/refresh`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
  if (!res.ok) {
    const body = await safeJson(res)
    throw new ApiError(res.status, body?.code ?? null, body?.detail ?? '')
  }
  return (await res.json()) as TokenPairResponse
}

async function refreshOnce(): Promise<TokenPairResponse> {
  if (!inflightRefresh) {
    inflightRefresh = performRefresh().finally(() => {
      inflightRefresh = null
    })
  }
  return inflightRefresh
}

async function safeJson(res: Response): Promise<ApiErrorBody | null> {
  try {
    return (await res.json()) as ApiErrorBody
  } catch {
    return null
  }
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = getApiBaseUrl()
  const url = `${base}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    skipAuth = false,
    isRetry = false,
    keepalive = false,
  } = options

  // FormData шлём как есть — Content-Type с boundary выставит браузер.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData

  const headers: Record<string, string> = {}
  if (!skipAuth) {
    const token = getAccessToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json'

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    keepalive,
  })

  if (res.status === 401 && !skipAuth && !isRetry) {
    const errorBody = await safeJson(res)
    // TOKEN_REUSE — вся цепочка отозвана, refresh не поможет.
    if (errorBody?.code === 'TOKEN_REUSE') {
      onSessionExpired(errorBody.code)
      throw new ApiError(401, 'TOKEN_REUSE', errorBody.detail)
    }
    try {
      const pair = await refreshOnce()
      onTokenRefreshed(pair)
      return await request<T>(path, { ...options, isRetry: true })
    } catch (err) {
      onSessionExpired(err instanceof ApiError ? err.code : null)
      if (err instanceof ApiError) throw err
      throw new ApiError(
        401,
        'INVALID_TOKEN',
        i18n.t(($) => $.errors.sessionExpired),
      )
    }
  }

  if (res.status === 204) return undefined as T
  if (!res.ok) {
    const errorBody = await safeJson(res)
    throw new ApiError(res.status, errorBody?.code ?? null, errorBody?.detail ?? '')
  }
  return (await res.json()) as T
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  postForm: <T>(path: string, form: FormData, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body: form }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
